import React, { useMemo, useRef, useState } from "react";
import { Dimensions, StyleSheet, TouchableOpacity, View } from "react-native";
import { useNavigation } from "@react-navigation/native";
import Text from "@components/CustomText";
import color from "@constants/color";
import LinearGradient from "react-native-linear-gradient";
import ChevronRight from "@assets/svg/ChevronRight";
// @ts-ignore
import Carousel, { Pagination } from "react-native-snap-carousel";
import LoadingComponent from "./LoadingComponent";

interface InfoCardItem {
    title?: string;
    caption?: string;
    value?: string | number;
    icon?: string;
    enabled?: boolean;
    nav_to?: string;
    nav_params?: Record<string, unknown>;
    nav_value?: string | number;
    loading?: string[];
    [key: string]: any;
}

interface InfoCardsProps {
    data:
    | InfoCardItem[]
    | {
        value?: InfoCardItem[] | { list?: InfoCardItem[] };
        list?: InfoCardItem[];
    }
    | InfoCardItem;
    onPress?: (item?: InfoCardItem, index?: number) => void;
}

const InfoCards: React.FC<InfoCardsProps> = ({ data }) => {

    const navigation = useNavigation<any>();
    const carouselRef = useRef<any>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const { width: screenWidth } = Dimensions.get("window");

    const cards = useMemo<InfoCardItem[]>(() => {
        if (!data) return [];
        if (Array.isArray(data)) return data as InfoCardItem[];

        const value = (data as any)?.value;
        if (Array.isArray((data as any)?.list)) {
            return (data as any).list as InfoCardItem[];
        }
        if (Array.isArray(value?.list)) {
            return value.list as InfoCardItem[];
        }
        if (Array.isArray(value)) {
            return value as InfoCardItem[];
        }
        if (data && typeof data === "object") {
            return [data as InfoCardItem];
        }
        return [];
    }, [data]);

    const enabledCards = cards.filter(
        (card) => card && (card.enabled === undefined || card.enabled === true),
    );

    if (enabledCards.length === 0) return null;

    const handlePress = (item: InfoCardItem, index: number) => {
        if (item?.nav_to) {
            navigation.navigate(item.nav_to, {
                nav_params: item?.nav_params,
                nav_value: item?.nav_value,
            });
        }
    };

    const renderCard = (item: InfoCardItem, index: number) => (
        <TouchableOpacity
            key={`${item?.title || "info-card"}-${index}`}
            activeOpacity={0.85}
            disabled={!item?.nav_to}
            onPress={() => handlePress(item, index)}
            style={styles.touchable}
        >
            <LinearGradient
                colors={item?.background || ["#0D7CFF", "#3FB3FF"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.card}
            >
                <View style={styles.content}>
                    <View style={styles.textBlock}>
                        <View style={styles.titleRow}>
                            {(item?.value !== undefined || item?.loading?.includes?.("value")) ? (
                                <LoadingComponent
                                    visible={!item?.loading?.includes?.("value")}
                                    style={styles.loadingValue}
                                >
                                    {item?.value !== undefined ? (
                                        <Text variant="heading_24" style={styles.value} numberOfLines={1}>
                                            {item.value}
                                        </Text>
                                    ) : null}
                                </LoadingComponent>
                            ) : null}
                            {(item?.title || item?.loading?.includes?.("title")) ? (
                                <LoadingComponent
                                    visible={!item?.loading?.includes?.("title")}
                                    style={styles.loadingTitle}
                                >
                                    {item?.title ? (
                                        <Text variant="title_18" style={styles.title} numberOfLines={1}>
                                            {item.title}
                                        </Text>
                                    ) : null}
                                </LoadingComponent>
                            ) : null}
                        </View>
                        {(item?.caption || item?.loading?.includes?.("caption")) ? (
                            <LoadingComponent
                                visible={!item?.loading?.includes?.("caption")}
                                style={styles.loadingCaption}
                            >
                                {item?.caption ? (
                                    <Text variant="body_text_3" style={styles.caption} numberOfLines={1}>
                                        {item.caption}
                                    </Text>
                                ) : null}
                            </LoadingComponent>
                        ) : null}
                    </View>
                    {item?.nav_to ? <View style={styles.cta}>
                        <View style={styles.chevronWrapper}>
                            <ChevronRight pathFill="#777777" width={18} height={18} />
                        </View>
                    </View> : null}
                </View>
            </LinearGradient>
        </TouchableOpacity>
    );

    if (enabledCards.length === 1) {
        return <View style={styles.container}>{renderCard(enabledCards[0], 0)}</View>;
    }

    return (
        <View style={styles.carouselContainer}>
            <Carousel
                layout="default"
                data={enabledCards}
                ref={carouselRef}
                renderItem={({ item, index }: { item: InfoCardItem; index: number }) =>
                    renderCard(item, index)
                }
                sliderWidth={screenWidth}
                itemWidth={screenWidth - 64}
                inactiveSlideScale={0.95}
                inactiveSlideOpacity={0.8}
                enableSnap
                loop
                loopClonesPerSide={enabledCards.length}
                onSnapToItem={(idx: number) => setCurrentIndex(idx)}
                activeSlideAlignment="center"
                contentContainerCustomStyle={styles.carouselContent}
            />
            <Pagination
                dotsLength={enabledCards.length}
                activeDotIndex={currentIndex}
                inactiveDotStyle={styles.inactiveDot}
                dotStyle={styles.activeDot}
                dotColor="#444"
                inactiveDotColor="#A0A0A0"
                inactiveDotScale={1}
                carouselRef={carouselRef}
                dotContainerStyle={{ marginHorizontal: 3 }}
                containerStyle={styles.pagination}
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        width: "100%",
        paddingHorizontal: 6,
        marginVertical: 8,
    },
    carouselContainer: {
        width: "100%",
        height: 120
    },
    carouselContent: {
        paddingHorizontal: 6,
        height: 100
    },
    touchable: {
        width: "100%",
    },
    card: {
        borderRadius: 18,
        paddingHorizontal: 16,
        paddingVertical: 14,
    },
    content: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },
    textBlock: {
        flex: 1,
        paddingRight: 8,
    },
    titleRow: {
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
    },
    title: {
        color: color.white,
        marginLeft: 6,
        width: "100%",
    },
    caption: {
        color: "#E6F2FF",
    },
    value: {
        color: color.white,
    },
    loadingValue: {
        marginVertical: 2,
        width: "50%",
        maxWidth: "70%",
    },
    loadingTitle: {
        marginVertical: 2,
        width: "70%",
    },
    loadingCaption: {
        marginTop: 4,
        width: "80%",
    },
    cta: {
        justifyContent: "center",
        alignItems: "center",
    },
    chevronWrapper: {
        backgroundColor: color.Gray,
        padding: 6,
        borderRadius: 10,
    },
    pagination: {
        paddingVertical: 4,
    },
    inactiveDot: {
        height: 6,
        width: 6,
        borderRadius: 3,
    },
    activeDot: {
        height: 8,
        width: 16,
        borderRadius: 8,
    },
});

export default InfoCards;